"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function GetInTouch() {
  return <section data-section-id="22241" id="start-your-project-form" className="dark bg-background text-foreground py-24 md:py-48 px-4 md:px-8 w-full overflow-hidden">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-sans text-5xl md:text-8xl font-black text-center mb-16 md:mb-24 uppercase tracking-tighter text-foreground">
          Start Your Project
        </h2>

        <FormProvider formId="get_in_touch_home2" sectionName="get-in-touch" className="w-full" defaultValues={{
        name: "",
        email: "",
        company_type: "",
        phone: "",
        project_details: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        company_type: {
          required: false
        },
        phone: {
          required: false,
          pattern: "phone"
        },
        project_details: {
          required: true
        }
      }} submitText="Initiate Project" successMessage="Thank you. Our team will reach out to you shortly." errorMessage="Something went wrong. Please try again.">
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 border border-white/10 bg-white/5 backdrop-blur-xl text-center animate-in fade-in zoom-in duration-500">
        <h3 className="font-sans text-3xl font-bold mb-4 text-foreground uppercase tracking-tight">Request Received</h3>
        <p className="font-mono text-xs text-muted-foreground uppercase tracking-[0.2em]">
          {successMessage}
        </p>
      </div>;
  }
  const inputClasses = "border-0 border-b border-white/20 rounded-none px-0 py-4 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-secondary transition-all duration-500 placeholder:text-white/20 h-auto text-xl md:text-2xl";
  const labelClasses = "font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block";
  return <Form onSubmit={handleSubmit} className="space-y-12 md:space-y-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-12 md:gap-y-16">
        
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@moondos.media" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        
        <FormField control={control} name="company_type" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Industry Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={`${inputClasses} w-full justify-between`}>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-sm border border-white/10 bg-popover/90 backdrop-blur-md shadow-2xl">
                  {["Corporate", "Athlete / Influencer", "Creative Agency", "Startup", "Other"].map((option, i) => <SelectItem key={option} value={option} data-index={i} className="font-mono text-[10px] uppercase tracking-widest focus:bg-white/10 focus:text-white cursor-pointer py-3">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />
      </div>

      
      <FormField control={control} name="project_details" render={({
      field
    }) => <FormItem className="space-y-0">
            <FormLabel className={labelClasses}>Project Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your brand vision, goals, and timeline" {...field} className={`${inputClasses} min-h-[120px] resize-none`} />
            </FormControl>
            <p className="mt-2 font-mono text-[10px] text-muted-foreground uppercase tracking-widest opacity-60">
              Precision in your vision leads to excellence in our execution.
            </p>
            <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
          </FormItem>} />

      
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive font-mono text-[10px] uppercase tracking-widest">
          {submitError}
        </div>}

      
      <div className="pt-8">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 rounded-sm bg-primary hover:bg-secondary text-primary-foreground font-sans font-black text-lg tracking-[0.2em] uppercase transition-all duration-500 border-none shadow-none hover:shadow-2xl">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : "Initiate Project"}
        </Button>
      </div>
    </Form>;
}